import { MouseEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { useCreateFolder, useCreateNewTask, useCreateTag } from '@nx-frontend/shared/hooks';
import { useCreateNote } from '@nx-frontend/shared/hooks/use-create/use-note-create';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  ContextMenuItemData,
  MenuItem,
  MenuItemType,
  UiContextMenuConfigPosition,
  UiContextMenuTheme,
} from '@nx-frontend/shared/types';
import { contextItemLocMap, pageMenuItem, subfolderMenuItem, taskMenuItem } from './context-items';

export const useOpenKitchenCreateContext = () => {
  const [currentWorkspace] = useUnit([$currentWorkspace]);
  const [openContextMenu] = useEvent([openContextMenuEvent]);
  const createFolder = useCreateFolder();
  const createNote = useCreateNote();
  const createNewTask = useCreateNewTask();
  const createTag = useCreateTag();

  const t = useTranslations();

  return useCallback(
    async (menuItem: MenuItem, e: MouseEvent) => {
      const items: ContextMenuItemData[] = [];
      const workspaceId = currentWorkspace.globalId;

      if (menuItem.type === MenuItemType.FOLDERS) {
        createFolder({ workspaceId, parentId: 'root' });
        return;
      }

      if (menuItem.type === MenuItemType.TAGS) {
        createTag({ workspaceId, openNotesByTag: true });
        return;
      }

      items.push(
        Object.assign({}, subfolderMenuItem, {
          title: subfolderMenuItem.id ? t(contextItemLocMap[subfolderMenuItem.id]) : subfolderMenuItem.title,
          action: () => {
            createFolder({ workspaceId, parentId: menuItem.id.replace('notesFolder#', '') });
          },
        })
      );

      items.push(
        Object.assign({}, pageMenuItem, {
          title: pageMenuItem.id ? t(contextItemLocMap[pageMenuItem.id]) : pageMenuItem.title,
          action: () => {
            createNote({
              workspaceId,
              parentId: menuItem.id.replace('notesFolder#', ''),
              redirectAfterCreate: true,
            });
          },
        })
      );

      items.push(
        Object.assign({}, taskMenuItem, {
          title: taskMenuItem.id ? t(contextItemLocMap[taskMenuItem.id]) : taskMenuItem.title,
          action: () => {
            createNewTask({
              workspaceId,
              folderId: menuItem.id.replace('notesFolder#', ''),
            });
          },
        })
      );

      openContextMenu({
        items,
        position: calculateContextMenuPosition(e.target, 4),
        config: {
          position: UiContextMenuConfigPosition.BOTTOM_RIGHT,
          menuPadding: 0,
          theme: UiContextMenuTheme.LIGHT,
        },
      });
    },
    [createFolder, createNewTask, createNote, createTag, currentWorkspace.globalId, openContextMenu, t]
  );
};
